import { mediaQueryLarge } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

const LAYOUTS = {
  mobile: ['mobile-single', 'default'],
  desktop: ['columns-2', 'columns-3', 'columns-4'],
};

/** Collection and search layout, with independent mobile/desktop preferences. */
export default class ResultsList extends PaginatedList {
  #preferences = {};
  #resizeObserver = new ResizeObserver(() => this.#scheduleLayout());
  #contentObserver = new MutationObserver(() => this.#scheduleLayout());
  #frame = 0;
  #observedGrid;

  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', this.#scheduleLayout);
    window.addEventListener('popstate', this.#restoreHistory);
    this.#contentObserver.observe(this, { childList: true, subtree: true });
    this.#restoreLayout();
    this.setAttribute('initialized', '');
  }

  updatedCallback() {
    super.updatedCallback();
    this.#scheduleLayout();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#scheduleLayout);
    window.removeEventListener('popstate', this.#restoreHistory);
    this.#resizeObserver.disconnect();
    this.#contentObserver.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#observedGrid = undefined;
  }

  #viewport() {
    return mediaQueryLarge.matches ? 'desktop' : 'mobile';
  }

  #readPreference(viewport) {
    if (this.#preferences[viewport]) return this.#preferences[viewport];
    try {
      const saved = sessionStorage.getItem(`product-grid-view-${viewport}`);
      if (LAYOUTS[viewport].includes(saved)) return saved;
    } catch {
      // Private browsing/storage restrictions must not prevent layout changes.
    }
    return viewport === 'desktop' ? 'columns-4' : this.refs.grid?.dataset.defaultMobileView || 'default';
  }

  /** Native radios supply both keyboard and touch interaction. */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    const viewport = target.name === 'grid-mobile' ? 'mobile' : 'desktop';
    if (!LAYOUTS[viewport].includes(target.value)) return;
    this.#preferences[viewport] = target.value;
    try {
      sessionStorage.setItem(`product-grid-view-${viewport}`, target.value);
    } catch {
      // Retain this page's preference in memory if storage is unavailable.
    }
    this.#restoreLayout();
  }

  /** Toolbar sorting retains active facets and search terms. */
  updateSort({ target }) {
    if (!(target instanceof HTMLSelectElement)) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', target.value);
    url.searchParams.delete('page');
    const form = this.querySelector('facets-form-component');
    if (form && typeof form.updateFiltersByURL === 'function') form.updateFiltersByURL(url.toString());
  }

  retryFilters() {
    const form = this.querySelector('facets-form-component');
    if (form && typeof form.refreshFromURL === 'function') form.refreshFromURL();
  }

  #restoreHistory = () => this.retryFilters();

  #scheduleLayout = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      if (this.isConnected) this.#restoreLayout();
    });
  };

  #restoreLayout() {
    const grid = this.refs.grid;
    const viewport = this.#viewport();
    const controls = this.querySelectorAll(`input[name="${viewport === 'mobile' ? 'grid-mobile' : 'grid'}"]`);
    if (controls.length) {
      const layout = this.#readPreference(viewport);
      if (grid && grid.getAttribute('product-grid-view') !== layout) grid.setAttribute('product-grid-view', layout);
      for (const control of controls) control.checked = control.value === layout;
    }
    if (grid !== this.#observedGrid) {
      this.#resizeObserver.disconnect();
      if (grid) this.#resizeObserver.observe(grid);
      this.#observedGrid = grid;
    }
    if (!grid) return;

    // Read layout once per update, never on scroll. Product detail media retains
    // its independent responsive sizes; only images inside this grid are managed.
    const card = grid.querySelector('.product-grid__item');
    const width = Math.ceil(card?.getBoundingClientRect().width || 0);
    if (!width) return;
    for (const image of grid.querySelectorAll('.card-gallery img')) {
      const sizes = `${image.loading === 'lazy' ? 'auto, ' : ''}${width}px`;
      if (image.sizes !== sizes) image.sizes = sizes;
      if (!image.hasAttribute('data-grid-image-sized')) image.setAttribute('data-grid-image-sized', '');
    }
  }
}

if (!customElements.get('results-list')) customElements.define('results-list', ResultsList);
